import React, { useEffect, useState } from 'react'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import GetAppIcon from '@mui/icons-material/GetApp'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import LinkIcon from '@mui/icons-material/Link'
import Fade from '@mui/material/Fade'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import { GlobalStyles } from '@mui/material'
import { useSelector } from 'react-redux'
import { useAuth0 } from '../../contexts/auth0-context'
import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import download from 'downloadjs'
import { EnhancedTableHead, getComparator, stableSort } from '../../utilities/helpers'
import { EmptyPlaceholder } from '../../shared/components/EmptyPlaceholder'
import { SortableTable } from '../../shared/components/SortableTable'
import { useGetDraftFile } from '../../shared/queries/useGetDraftFile'
import DraftEditDialog from './DraftEditDialog'
import { ReactComponent as MonitorUploadIcon } from '../../assets/images/monitor_upload.svg'
import PubMedLogo from '../../assets/images/pubmed.png'
import SvgIcon from '@mui/material/SvgIcon'

const columns = [
    { id: 'index', label: '' },
    { id: 'citation', label: 'Citation' },
    { id: 'name', label: 'Title' },
    { id: 'status', label: 'Status', align: 'center' as const, headPad: 2 },
    { id: 'link', label: 'Link', align: 'center' as const, headPad: 2.5 },
    { id: 'originalFileName', label: 'File', align: 'center' as const, headPad: 2.5 },
    { id: 'year', label: 'Year', align: 'center' as const, headPad: 2.5 },
    { id: 'source', label: 'Source', align: 'center' as const, headPad: 2 },
    { id: 'panel', label: '' },
]

const DownloadDraftFileButton = ({ draft }: { draft: any }) => {
    const { refetch: getDraftFile } = useGetDraftFile(draft.id)

    const handleDownload = async () => {
        const { data: draftFile } = await getDraftFile()
        await download(draftFile, draft.originalFileName, (draftFile as any).type)
    }

    return <GetAppIcon sx={{ color: 'figma.primary', cursor: 'pointer' }} onClick={handleDownload} />
}

const STATUS_COLORS: Record<string, string> = {
    draft: 'figma.grayscale.tier4',
    inreview: 'figma.accentColors.orange',
    rejected: 'figma.accentColors.red',
    accepted: 'figma.accentColors.green',
}

function getStatusColor(status: string): string {
    const normalized = (status || 'draft').toLowerCase().replace(/[\s_-]/g, '')
    return STATUS_COLORS[normalized] ?? STATUS_COLORS['draft']
}

interface Props {
    onDraftCountChange: (count: number) => void
    refreshKey: number
}

export default function DraftTable({ onDraftCountChange, refreshKey }: Props) {
    const { isAuthenticated, getTokenSilently } = useAuth0()
    const strategyId = useSelector((state: any) => state.strategy._id)

    const [drafts, setDrafts] = useState<any[]>([])
    const [loaded, setLoaded] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [order, setOrder] = useState('asc')
    const [orderBy, setOrderBy] = useState('')
    const [selectedDraft, setSelectedDraft] = useState<any>(null)
    const [editOpen, setEditOpen] = useState(false)

    useEffect(() => {
        if (!isAuthenticated || !strategyId) return
        fetchDrafts()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, strategyId, refreshKey])

    async function fetchDrafts() {
        setLoaded(false)
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const res = await callAPI(
                endpoints.getDraftsByStrategyId.url + strategyId,
                endpoints.getDraftsByStrategyId.method,
                token
            )
            const data = res?.data || []
            setDrafts(data)
            onDraftCountChange(data.length)
        } catch (err) {
            console.error('Failed to fetch drafts:', err)
            setDrafts([])
        } finally {
            setLoaded(true)
        }
    }

    const dataRows = drafts.map((d) => ({
        citation: d.shortCitation || d.citation,
        name: d.name,
        source: !!d.pubmedId,
        link: d.link,
        year: d.year,
        originalFileName: d.originalFileName,
        status: d.status || 'draft',
        id: d._id,
        _draft: d,
    }))

    const handleRowClick = async (rowData: any) => {
        const draft = rowData._draft
        setSelectedDraft(draft)
        setEditOpen(true)

        if (draft.status === 'draft') {
            try {
                const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
                const res = await callAPI(endpoints.updateDraft.url + draft._id, endpoints.updateDraft.method, token, {
                    ...draft,
                    status: 'in review',
                })
                if (res?.success) {
                    setDrafts((prev) => prev.map((d) => (d._id === draft._id ? res.data : d)))
                    setSelectedDraft(res.data)
                }
            } catch (err) {
                console.error('Failed to update draft status to in review:', err)
            }
        }
    }

    const handleSaved = (updatedDraft: any) => {
        setDrafts((prev) => prev.map((d) => (d._id === updatedDraft._id ? updatedDraft : d)))
    }

    const handleDeleted = (id: string) => {
        const updated = drafts.filter((d) => d._id !== id)
        setDrafts(updated)
        onDraftCountChange(updated.length)
    }

    const sortedRows = stableSort(dataRows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    )

    const globalStyles = <GlobalStyles styles={{ '.MuiTableCell-root': { borderBottom: 'none !important' } }} />

    return (
        <>
            {globalStyles}
            <Fade in={loaded} timeout={800}>
                <Grid container item xs>
                    {loaded && dataRows.length === 0 && (
                        <EmptyPlaceholder
                            title="No drafts to review"
                            subtitle="Import evidence from PubMed to create drafts"
                        />
                    )}
                    {loaded && dataRows.length > 0 && (
                        <>
                            <TableContainer>
                                <Table stickyHeader aria-label="drafts table">
                                    <EnhancedTableHead
                                        columns={columns}
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={(_: any, prop: string) => {
                                            const isAsc = orderBy === prop && order === 'asc'
                                            setOrder(isAsc ? 'desc' : 'asc')
                                            setOrderBy(prop)
                                        }}
                                    />
                                    <SortableTable.Body data={sortedRows} onSort={() => {}} onDragStart={() => {}}>
                                        {({ rowData, index, rowProps }: any) => (
                                            <SortableTable.Row {...rowProps}>
                                                {columns.map((column) => {
                                                    const value = rowData[column.id]

                                                    if (column.id === 'index') {
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                sx={{
                                                                    color: '#808080',
                                                                    border: 'none',
                                                                    borderTopLeftRadius: index === 0 ? 14 : 0,
                                                                    borderBottomLeftRadius:
                                                                        index === sortedRows.length - 1 ? 14 : 0,
                                                                }}
                                                            >
                                                                <Typography variant={'body9' as any}>
                                                                    {index + page * rowsPerPage + 1}.
                                                                </Typography>
                                                            </TableCell>
                                                        )
                                                    } else if (column.id === 'status') {
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align="center"
                                                                sx={{ border: 'none' }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 16,
                                                                        height: 16,
                                                                        borderRadius: '50%',
                                                                        bgcolor: getStatusColor(value),
                                                                        display: 'inline-block',
                                                                    }}
                                                                />
                                                            </TableCell>
                                                        )
                                                    } else if (column.id === 'source') {
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align="center"
                                                                sx={{ border: 'none' }}
                                                            >
                                                                {value ? (
                                                                    <Box
                                                                        component="img"
                                                                        src={PubMedLogo}
                                                                        alt="PubMed"
                                                                        sx={{
                                                                            width: 28,
                                                                            height: 28,
                                                                            objectFit: 'contain',
                                                                            filter: 'grayscale(1) brightness(0.3)',
                                                                            verticalAlign: 'middle',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <SvgIcon
                                                                        component={MonitorUploadIcon}
                                                                        inheritViewBox
                                                                        sx={{
                                                                            color: 'figma.grayscale.tier2',
                                                                            fontSize: 20,
                                                                        }}
                                                                    />
                                                                )}
                                                            </TableCell>
                                                        )
                                                    } else if (column.id === 'originalFileName') {
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align="center"
                                                                sx={{ border: 'none' }}
                                                            >
                                                                {value && value.length > 0 ? (
                                                                    <DownloadDraftFileButton draft={rowData} />
                                                                ) : null}
                                                            </TableCell>
                                                        )
                                                    } else if (column.id === 'link' && value && value.length > 0) {
                                                        const formattedLink =
                                                            value.includes('http://') || value.includes('https://')
                                                                ? value
                                                                : 'http://' + value
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align={column.align}
                                                                sx={{ border: 'none' }}
                                                            >
                                                                <Link
                                                                    underline="none"
                                                                    href={formattedLink}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <LinkIcon />
                                                                </Link>
                                                            </TableCell>
                                                        )
                                                    } else if (column.id === 'panel') {
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                sx={{
                                                                    border: 'none',
                                                                    borderTopRightRadius: index === 0 ? 14 : 0,
                                                                    borderBottomRightRadius:
                                                                        index === sortedRows.length - 1 ? 14 : 0,
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => handleRowClick(rowData)}
                                                            >
                                                                <IconButton
                                                                    sx={(theme) => ({
                                                                        padding: theme.spacing(1.5),
                                                                        marginInline: theme.spacing(-0.75),
                                                                    })}
                                                                >
                                                                    <ArrowForwardIosIcon color="primary" />
                                                                </IconButton>
                                                            </TableCell>
                                                        )
                                                    } else {
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align={column.align}
                                                                sx={{ border: 'none' }}
                                                                style={{ whiteSpace: 'break-spaces' }}
                                                            >
                                                                <Typography variant="body1">{value}</Typography>
                                                            </TableCell>
                                                        )
                                                    }
                                                })}
                                            </SortableTable.Row>
                                        )}
                                    </SortableTable.Body>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={dataRows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(+e.target.value)
                                    setPage(0)
                                }}
                            />
                        </>
                    )}
                </Grid>
            </Fade>

            {selectedDraft && (
                <DraftEditDialog
                    draft={selectedDraft}
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    onSaved={handleSaved}
                    onDeleted={handleDeleted}
                />
            )}
        </>
    )
}
